import { Address } from "@msuite/ui/address";
import { Link } from "@msuite/ui/link";
import { localized } from "@msuite/ui/localization";
import { ProgressMeter } from "@msuite/ui/progress-meter";
import { createColumnHelper, createMeta, createRowActions } from "@msuite/ui/table";
import { WorkspaceTag } from "@msuite/ui/tag";
import { formatNumber, guardNumber } from "@msuite/utils/numbers";
import { getAddressString, guardString } from "@msuite/utils/strings";
import type { UserOverviewMeasurement } from "@msuite/utils/types";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<UserOverviewMeasurement>();

export const columns = [
	columnHelper.accessor("workspaceName", {
		header: localized("organization.workspace"),
		id: "workspaceName",
		cell: (cell) => (
			<WorkspaceTag
				workspace={{
					name: cell.row.original.workspaceName,
					color: cell.row.original.workspaceColor,
				}}
			/>
		),
	}),
	columnHelper.accessor("number", {
		id: "number",
		header: localized("projects.fields.project_number"),
		cell: (cell) => guardString(cell.getValue()),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor("name", {
		id: "name",
		header: localized("projects.fields.project_name"),
		cell: (cell) => (
			<Link href={`/projects/${cell.row.original.id}`}>{guardString(cell.getValue())}</Link>
		),
	}),
	columnHelper.accessor((row) => getAddressString(row), {
		id: "address",
		header: localized("address.address"),
		cell: (cell) => <Address item={cell.row.original} />,
	}),
	columnHelper.accessor("clientName", {
		id: "clientName",
		header: localized("projects.fields.client"),
		cell: (cell) => (
			<Link href={`/contacts/${cell.row.original.clientId}`}>{guardString(cell.getValue())}</Link>
		),
	}),
	columnHelper.accessor("taCount", {
		id: "ticketAssignmentCount",
		header: localized("measurements.ticket_assignments_total"),
		cell: (cell) => guardNumber(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("tamsCount", {
		id: "tamsCount",
		header: localized("measurements.ticket_assignment_measurement_statements_total"),
		cell: (cell) => guardNumber(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("totalInvoiceSum", {
		id: "totalInvoiceSum",
		header: localized("controlling.total_invoice_sum"),
		cell: (cell) => formatNumber(cell.getValue(), "currency"),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor(
		(row) => {
			const taCount = guardNumber(row.taCount);
			const tamsCount = guardNumber(row.tamsCount);
			return guardNumber(((tamsCount / taCount) * 100).toFixed(2));
		},
		{
			id: "progress",
			size: 250,
			enableResizing: false,
			header: localized("general.progress"),
			cell: (cell) => {
				return (
					<div className="flex-center h-full pr-4">
						<ProgressMeter value={cell.getValue()} />
					</div>
				);
			},
		},
	),
	createRowActions(RowActions),
];
